'use client';
import React from 'react';
import { Icon } from '@benzinga/core-ui';
import { Search } from '@benzinga/search-ui';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import type { Ticker } from '@benzinga/session';
import { ChartDataType } from './types';
import { useTranslation } from 'react-i18next';
import { Close as CloseIcon } from '@benzinga/icons';

interface SymbolMenuProps {
  symbols: string[];
  chartData: ChartDataType;
  currentSymbol: string;
  onAddSymbol: (symbol: string) => void;
  onRemoveSymbol: (symbol: string) => void;
}

export const SymbolMenu = ({ chartData, currentSymbol, onAddSymbol, onRemoveSymbol, symbols }: SymbolMenuProps) => {
  const { t } = useTranslation('quote');
  const [symbolSearch, setSymbolSearch] = React.useState<string>('');

  const handleSearchSelect = React.useCallback(
    (selected: Ticker) => {
      if (symbols.includes(symbolSearch.toUpperCase())) {
        setSymbolSearch('');
        return;
      }
      onAddSymbol(selected.symbol);
    },
    [onAddSymbol, symbolSearch, symbols],
  );

  const handleRemoveSymbol = (symbol: string) => {
    if (currentSymbol === symbol) return;
    onRemoveSymbol(symbol);
  };

  return (
    <div className="h-full border border-b-0 border-bzblue-300 bg-white md:border-b md:border-l-0">
      <div className="flex items-center gap-2 border-b border-bzblue-300 px-4 py-2">
        <Icon icon={faPlus} />
        <Search
          onSelect={handleSearchSelect}
          placeholder={t('Quote.Compare.compare')}
          searchType="ticker"
          variant="box"
        />
      </div>
      <div className="flex max-h-[310px] flex-col overflow-y-auto">
        {symbols
          .filter(symbol => symbol)
          .map(symbol => (
            <div className="ml-1 flex items-center justify-between gap-2 px-4 py-2" key={symbol}>
              <div className="flex items-center gap-2">
                <div
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: chartData?.[symbol]?.color }}
                />
                <div className="flex items-start gap-2 rounded border border-bzblue-400 bg-bzgray-100 px-1 py-0.5">
                  {symbol}
                </div>
              </div>
              {currentSymbol !== symbol && (
                <button
                  className="flex items-center justify-center w-6 h-6 rounded-full hover:bg-gray-200"
                  onClick={() => handleRemoveSymbol(symbol)}
                  aria-label="Remove symbol"
                >
                  <CloseIcon className="float-right" />
                </button>
              )}
            </div>
          ))}
      </div>
    </div>
  );
};

export default SymbolMenu;
